import type Stripe from 'stripe-latest';
import { capitalizeWord } from '../../../utils';
import type { ViewIntervalOptions } from './plans';

export const isFreePlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'FREE';
export const isBusinessPlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'BUSINESS';
export const isEnterprisePlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'ENTERPRISE';
export const getLimitDisplayValue = (value: string | undefined) => value ? capitalizeWord(value) : '-';
export const planIcon = (product: Stripe.Product) => {
  if (isFreePlan(product)) {
    return '/img/icon-user.svg';
  }

  if (isBusinessPlan(product)) {
    return '/img/icon-briefcase.svg';
  }

  if (isEnterprisePlan(product)) {
    return '/img/icon-building.svg';
  }

  return '';
};

/**
 * Formats a given price to a string representation in monthly terms.
 *
 * @param {number} price - The price to be formatted (in cents).
 * @param {ViewIntervalOptions} viewInterval - The interval option to view the price.
 *        If 'year', the price is converted from yearly to monthly.
 * @returns {string} The formatted price string in the format `$<amount>/month`.
 */
export const formatPrice = (price: number, viewInterval: ViewIntervalOptions): string => {
  // for decimal conversion
  let divisor = 100;

  // for yearly to monthly conversion
  if (viewInterval === 'year') {
    divisor *= 12;
  }

  const amount = Math.round(price / divisor);
  return `$${amount}/month`;
};

/**
 * Calculate the discount percentage between the original and new value
 *
 * @param {number} orgValue - The original value before discount
 * @param {number} newValue - The value after discount has been applied
 * @returns {number} The discount percentage
 */
export const calculateDiscountPercentage = (orgValue: number, newValue: number): number => {
  if (orgValue < 1) {
    return 0;
  }
  const discount = orgValue - newValue;

  // The + converts the string from toFixed(2) back to a number
  return +((discount / orgValue) * 100).toFixed(2);
};
